import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import Model from "@tripian/model";
import { CustomSlider, PreLoading, SvgIcons } from "@tripian/react";
import TourCard from "../../../../../../components/TourCard/TourCard";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import useProgressiveTourApi from "../../../../../../hooks/useProgressiveTourApi";
import classes from "./BookableExperiencesContainer.module.scss";

const CATEGORIES: { id: string; label: Model.TranslationKey }[] = [
  { id: "all", label: "trips.categories.all" },
  { id: "adventure", label: "trips.myTrips.localExperiences.categories.adventure" },
  { id: "food", label: "trips.myTrips.localExperiences.categories.food" },
  { id: "culture", label: "trips.myTrips.localExperiences.categories.cultureHistory" },
  { id: "sight", label: "trips.myTrips.localExperiences.categories.sightSeeing" },
  { id: "art", label: "trips.myTrips.localExperiences.categories.artMuseum" },
  { id: "local", label: "trips.myTrips.localExperiences.categories.localNeighborhood" },
  { id: "events", label: "trips.myTrips.localExperiences.categories.events" },
  { id: "others", label: "trips.myTrips.localExperiences.categories.others" },
];

const PROVIDER_NAMES: Partial<Record<number, string>> = {
  [Model.PROVIDER_ID.GYG]: "GetYourGuide",
  [Model.PROVIDER_ID.VIATOR]: "Viator",
  [Model.PROVIDER_ID.VICTORY]: "Victory Live",
  [Model.PROVIDER_ID.TORISTY]: "Toristy",
  [Model.PROVIDER_ID.BOOK_BARBADOS]: "BookBarbados",
  [Model.PROVIDER_ID.REZDY]: "Rezdy",
  [Model.PROVIDER_ID.OCCASION_GENIUS]: "OccasionGenius",
  [Model.PROVIDER_ID.GOCITY]: "Gocity",
  [Model.PROVIDER_ID.CIVITATIS]: "Civitatis",
  [Model.PROVIDER_ID.LIVN]: "Livn",
};

// Elle tutulan listede olmayan provider'lar için site config'indeki adı kullan
const providerName = (providerId: number, names: Partial<Record<number, string>>) =>
  names[providerId] ??
  window.tconfig?.PROVIDERS?.tourAndTicket?.find(p => p.id === providerId)?.name;

const SORT_OPTIONS: { value: "asc" | "desc" | undefined; label: Model.TranslationKey }[] = [
  { value: undefined, label: "trips.filters.sortOptions.default" },
  { value: "asc", label: "trips.filters.sortOptions.priceLowToHigh" },
  { value: "desc", label: "trips.filters.sortOptions.priceHighToLow" },
];

interface IBookableExperiencesContainer {
  show: boolean;
  tripReference: Model.TripReference;
  t: (value: Model.TranslationKey) => string;
  onTourClick: (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => void;
}

const BookableExperiencesContainer: React.FC<IBookableExperiencesContainer> = ({
  show,
  tripReference,
  t,
  onTourClick,
}) => {
  const [searchText, setSearchText] = useState("");
  const [displayCount, setDisplayCount] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<"asc" | "desc" | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const debouncedSearch = useDebounce(searchText, 500);

  const availableProviders = useMemo<number[]>(() => {
    return (window.tconfig?.TOUR_TICKET_PROVIDER_IDS || []) as number[];
  }, []);

  React.useEffect(() => {
    if (availableProviders.length > 0 && selectedProviders.length === 0) {
      setSelectedProviders([...availableProviders]);
    }
  }, [availableProviders]);

  const providerIdParam = useMemo(() => {
    const ids = selectedProviders.length > 0 ? selectedProviders : availableProviders;
    return ids.length > 0 ? ids.join(",") : undefined;
  }, [selectedProviders, availableProviders]);

  const categoryParam = useMemo(() => {
    return selectedCategory === "all" ? undefined : selectedCategory;
  }, [selectedCategory]);

  const keywordsParam = useMemo(() => {
    const trimmed = debouncedSearch.trim();
    return trimmed.length >= 2 ? trimmed : undefined;
  }, [debouncedSearch]);

  // Reset display count when search params change
  useEffect(() => {
    setDisplayCount(10);
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [categoryParam, keywordsParam, sortBy, providerIdParam]);

  const { allTours, loading, isExhausted } = useProgressiveTourApi({
    cityId: String(tripReference.city.id),
    providerId: providerIdParam,
    lat: tripReference.city.coordinate.lat,
    lng: tripReference.city.coordinate.lng,
    date: tripReference.tripProfile.arrivalDatetime?.split(/[T ]/)[0],
    to: tripReference.tripProfile.departureDatetime?.split(/[T ]/)[0],
    adults: tripReference.tripProfile.numberOfAdults || 2,
    categories: categoryParam,
    keywords: keywordsParam,
    sortingBy: sortBy ? "price" : undefined,
    sortingType: sortBy,
    currentPage: Math.ceil(displayCount / 10) + 1, // +1 page lookahead so scroll stays smooth
    pageSize: 10,
  });

  const visibleTours = useMemo(() => allTours.slice(0, displayCount), [allTours, displayCount]);
  const canShowMore = displayCount < allTours.length;
  const hasNextPage = canShowMore || !isExhausted;

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && canShowMore) {
          setDisplayCount(prev => prev + 10);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [canShowMore]
  );

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (containerRef.current) containerRef.current.scrollTop = 0;
  };

  const handleProviderChange = (providerId: number) => {
    setSelectedProviders(prev =>
      prev.includes(providerId)
        ? prev.filter(p => p !== providerId)
        : [...prev, providerId]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSortChange = (value: "asc" | "desc" | undefined) => {
    setSortBy(value);
  };

  const containerClasses = [classes.bookableExperiencesContainer];
  if (!window.tconfig.SHOW_OVERVIEW)
    containerClasses.push("trip-layout-container-height");
  else containerClasses.push("trip-layout-container-height-tab");
  if (show) containerClasses.push(classes.containerOpen);
  else containerClasses.push(classes.containerClose);

  if (!show) return null;

  const showEmpty = isExhausted && allTours.length === 0;

  return (
    <div className={containerClasses.join(" ")}>
      <div className="flex flex-col h-full mb-16 md:mb-0">
        {/* Category tabs */}
        <div className="whitespace-nowrap py-4 border-b">
          <CustomSlider scrollSize={150}>
            {CATEGORIES.map((cat, index) => (
              <div
                key={index}
                className={`text-sm !w-auto rounded-3xl border border-solid border-gray-300 my-0 py-[0.4375rem] px-4 cursor-pointer min-w-fit font-medium whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? "text-white bg-primary-color"
                    : "text-black bg-white"
                }`}
                onClick={() => handleCategoryChange(cat.id)}
              >
                <span className="font-medium">{t(cat.label)}</span>
              </div>
            ))}
          </CustomSlider>
        </div>

        {/* Search + Filter bar */}
        <div className="relative py-4 border-b">
          <div className="flex items-center w-full">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("trips.myTrips.exploreMore.placeholder")}
                className="w-full ps-10 pe-10 py-3 border-2 border-gray-300 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:border-primary-color transition-colors"
                value={searchText}
                onChange={handleSearchChange}
              />
              <SvgIcons.Search className="absolute start-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
              {searchText && (
                <button
                  className="absolute end-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              )}
            </div>

            <div className="relative ms-2">
              <button
                ref={filterButtonRef}
                className="py-3 px-4 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center"
                onClick={() => setShowFilters(!showFilters)}
                aria-haspopup="true"
                aria-expanded={showFilters}
              >
                <SvgIcons.Filter2 className="me-1" />
                {t("trips.filters.title")}
              </button>

              {showFilters && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilters(false)}
                    style={{ pointerEvents: "all" }}
                  />
                  <div
                    className="fixed w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                    style={{
                      top:
                        (filterButtonRef.current?.getBoundingClientRect().bottom ?? 0) + 8,
                      right:
                        window.innerWidth -
                        (filterButtonRef.current?.getBoundingClientRect().right ?? 0),
                      maxHeight: "70vh",
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-4 border-b flex justify-between items-center bg-white z-10 rounded-t-lg">
                      <h3 className="font-semibold text-gray-800">
                        {t("trips.filters.title")}
                      </h3>
                      <div className="flex items-center">
                        <button
                          className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 hover:text-gray-900 transition-colors me-3"
                          onClick={() => {
                            setSelectedProviders([...availableProviders]);
                            setSortBy(undefined);
                            setShowFilters(false);
                          }}
                        >
                          {t("trips.filters.clearAll")}
                        </button>
                        <button
                          onClick={() => setShowFilters(false)}
                          className="text-gray-400 hover:text-gray-700 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                        >
                          <SvgIcons.SearchClose
                            width="18"
                            height="18"
                            className="text-gray-400 hover:text-gray-700"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      <div className="p-4 space-y-4">
                        {/* Sort */}
                        <div>
                          <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">
                            {t("trips.filters.sort")}
                          </h4>
                          <div className="space-y-1 ps-2">
                            {SORT_OPTIONS.map(option => (
                              <div
                                key={option.label}
                                className="flex items-center p-1.5 rounded-md cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSortChange(option.value)}
                              >
                                <div
                                  className={`w-4 h-4 rounded-full border-2 me-2 flex items-center justify-center flex-shrink-0 ${
                                    sortBy === option.value
                                      ? "border-primary-color"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {sortBy === option.value && (
                                    <div className="w-2 h-2 rounded-full bg-primary-color" />
                                  )}
                                </div>
                                <span
                                  className={`text-sm ${
                                    sortBy === option.value
                                      ? "text-primary-color font-medium"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {t(option.label)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Providers */}
                        {availableProviders.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">
                              {t("trips.filters.providers")}
                            </h4>
                            <div className="space-y-1 ps-2">
                              {availableProviders.map(providerId => (
                                <div
                                  key={providerId}
                                  className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ms-1.5"
                                >
                                  <div className="relative w-5 h-5">
                                    <input
                                      type="checkbox"
                                      id={`provider-${providerId}`}
                                      checked={selectedProviders.includes(providerId)}
                                      onChange={() => handleProviderChange(providerId)}
                                      className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                    />
                                    <SvgIcons.Check2
                                      className={`absolute start-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${
                                        selectedProviders.includes(providerId)
                                          ? "opacity-100"
                                          : "opacity-0"
                                      }`}
                                      fill="white"
                                    />
                                  </div>
                                  <label
                                    htmlFor={`provider-${providerId}`}
                                    className="ms-2.5 text-gray-700 cursor-pointer text-sm"
                                  >
                                    {providerName(providerId, PROVIDER_NAMES) ?? `Provider ${providerId}`}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tour list */}
        <div ref={containerRef} className="flex-1 overflow-auto py-4">
          {loading && allTours.length === 0 ? (
            <div className={classes.loadingContainer}>
              <div className="flex flex-col items-center justify-center">
                <PreLoading />
              </div>
            </div>
          ) : showEmpty ? (
            <div className="flex flex-col h-full items-center justify-center mt-10 text-gray-500">
              {t("trips.myTrips.localExperiences.toursEmpty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {visibleTours.map(tour => {
                const coverImage =
                  tour.images.find(img => img.is_cover)?.url ||
                  tour.images[0]?.url;
                return (
                  <TourCard
                    key={tour.productId}
                    data={tour}
                    title={tour.title}
                    price={tour.price > 0 ? tour.price : undefined}
                    currency={tour.currency}
                    rating={tour.rating > 0 ? tour.rating : undefined}
                    reviewCount={tour.ratingCount}
                    tags={tour.tags || []}
                    images={coverImage ? [coverImage] : []}
                    providerId={tour.providerId as Model.PROVIDER_ID}
                    onBook={() =>
                      onTourClick(
                        tour.providerId as Model.PROVIDER_ID,
                        tour.productId,
                        { url: tour.url }
                      )
                    }
                  />
                );
              })}
              {hasNextPage && (
                <div
                  ref={loadMoreRef}
                  className="h-10 flex items-center justify-center"
                >
                  {loading && !canShowMore && <PreLoading size="small" />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookableExperiencesContainer;